import axios from "axios"
import { Bounce, toast } from "react-toastify"

import { store } from "../store"
import endpoints from "./endpoints"
import {
  forceClientLogout,
  isLogoutInFlight,
} from "../helpers/sessionLogout"
import {
  isNetworkError,
  toastNetworkError,
} from "../helpers/networkError"
import {
  isServerError,
  toastServerError,
} from "../helpers/apiError"

export const http = axios.create({
  baseURL: process.env.REACT_APP_BASEURL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
})

http.interceptors.request.use(
  config => {
    const accessToken = store.getState().auth.jid
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      if (typeof config.headers?.delete === "function") {
        config.headers.delete("Content-Type")
      } else if (config.headers) {
        delete config.headers["Content-Type"]
      }
    }
    return config
  },
  error => Promise.reject(error)
)

http.interceptors.response.use(
  response => response,
  async error => {
    if (error?.response?.status === 401) {
      const url = error.config?.url || ""
      const isLoginRequest = url.includes(endpoints.login)
      const isLogoutRequest = url.includes(endpoints.logout)
      const isPublicRequest = url.includes("/api/v1/public/")
      const isSocketAuthRequest = url.includes("/api/v1/socket/auth")
      const hasSession = Boolean(store.getState().auth.jid)
      // Only force logout redirect for authenticated session expiry.
      // Guest pages (Create Account invite lookup, etc.) must not bounce to login.
      // Skip logout/socket-auth 401s and ignore while a logout is already in flight.
      if (
        !isLoginRequest &&
        !isLogoutRequest &&
        !isSocketAuthRequest &&
        !isPublicRequest &&
        hasSession &&
        !isLogoutInFlight()
      ) {
        forceClientLogout({ redirect: true, notifyServer: false })
      }
    }
    return Promise.reject(error)
  }
)

const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const getErrorMessage = error => {
  const data = error?.response?.data

  if (data?.errors && typeof data.errors === "object") {
    const messages = Object.values(data.errors)
      .flat()
      .map(msg => (typeof msg === "string" ? msg.trim() : ""))
      .filter(Boolean)
    if (messages.length > 0) {
      return messages.join(" ")
    }
  }

  if (typeof data?.message === "string" && data.message.trim()) {
    return data.message
  }

  if (error?.response?.status === 422) {
    return "Please check your input and try again."
  }

  if (isServerError(error)) {
    return "Something went wrong. Please try again."
  }

  return error?.message || "Request failed. Please try again."
}

const AUTH_FORM_401_PATHS = [
  endpoints.login,
  endpoints.register,
  endpoints.forgotPassword,
  endpoints.resetPassword,
  endpoints.verifyByOtp,
]

const shouldSkipErrorToast = error => {
  // Debounced search aborts the previous request; that is expected, not a failure.
  if (
    error?.code === "ERR_CANCELED" ||
    error?.name === "CanceledError" ||
    error?.name === "AbortError" ||
    axios.isCancel?.(error)
  ) {
    return true
  }
  if (error?.config?.skipErrorToast) return true
  if (error?.response?.status === 401) {
    const url = error.config?.url || ""
    const isAuthFormRequest = AUTH_FORM_401_PATHS.some(path =>
      url.includes(path)
    )
    // Session expiry redirects; credential / auth-form 401s should toast.
    return !isAuthFormRequest
  }
  return false
}

/**
 * Show a toast for API failures (unless skipErrorToast).
 * When skipErrorToast is set, return the error response so callers can
 * still read `data.message` for their own toast.
 */
const handleApiError = error => {
  if (isNetworkError(error)) {
    toastNetworkError()
  } else if (!shouldSkipErrorToast(error)) {
    const message = getErrorMessage(error)
    if (isServerError(error)) {
      toastServerError(message)
    } else {
      toast.error(message, TOAST_OPTIONS)
    }
  }
  if (error?.config?.skipErrorToast && error?.response) {
    return error.response
  }
  return null
}

const performRequest = async (method, url, data, config = {}) => {
  try {
    const response = await method(url, data, config)
    return response
  } catch (error) {
    return handleApiError(error)
  }
}

export const get = async (url, config = {}) => {
  return performRequest(http.get, url, config)
}

export const post = async (url, data, config = {}) => {
  return performRequest(http.post, url, data, config)
}

export const put = async (url, data, config = {}) => {
  return performRequest(http.put, url, data, config)
}

export const patch = async (url, data, config = {}) => {
  return performRequest(http.patch, url, data, config)
}

export const del = async (url, config = {}) => {
  return performRequest(http.delete, url, config)
}
